import React from 'react';

/**
 * 中国标准 BMI 阶段可视化组件
 * 说明：当前中国成人 BMI 分类标准不区分男女，因此男女共用同一套阈值
 * 简单实现：使用 div + CSS 渲染颜色区间和指示器
 */
const defaultThresholds = [
  { key: 'severe_thin', label: '中重度消瘦', start: 14, end: 16.5, color: '#ff4d4f' },
  { key: 'mild_thin', label: '轻度消瘦', start: 16.5, end: 18.5, color: '#ffec3d' },
  { key: 'normal', label: '正常', start: 18.5, end: 24, color: '#52c41a' },
  { key: 'overweight', label: '超重', start: 24, end: 28, color: '#faad14' },
  { key: 'obese', label: '肥胖', start: 28, end: 35, color: '#f5222d' },
];

const BMIScale = ({
  value,
  // sex 可预留扩展位，当前中国成人 BMI 阈值男女一致，因此未使用
  // sex,
  thresholds = defaultThresholds,
  unit = 'kg/m²',
  title = 'BMI 体重指数（中国标准）',
  showTicks = true,
  tickStep = 0.5,
  style,
}) => {
  if (!thresholds || thresholds.length === 0) return null;

  const min = thresholds[0].start;
  const max = thresholds[thresholds.length - 1].end;

  const safeValue = typeof value === 'number' && !Number.isNaN(value) ? value : null;
  const clampedValue = safeValue == null ? null : Math.min(Math.max(safeValue, min), max);
  const percent = clampedValue == null ? 0 : ((clampedValue - min) / (max - min)) * 100;

  const currentThreshold =
    safeValue == null
      ? null
      : thresholds.find(t => safeValue >= t.start && safeValue < t.end) || thresholds[thresholds.length - 1];

  const categoryLabel = currentThreshold ? currentThreshold.label : '暂无分级';

  const ticks = [];
  if (showTicks) {
    for (let v = min; v <= max + 0.0001; v += tickStep) {
      ticks.push(Number(v.toFixed(1)));
    }
  }

  // 重点刻度：仅在区间边界处显示数字，避免过于杂乱
  const majorTickValues = new Set(
    thresholds
      .map(t => [t.start, t.end])
      .flat()
      .map(v => Number(v.toFixed(1)))
  );

  return (
    <div
      style={{
        padding: 12,
        border: '1px solid #f0f0f0',
        borderRadius: 8,
        background: '#fff',
        fontSize: 12,
        ...style,
      }}
    >
      {/* 标题 + 当前值 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontWeight: 500 }}>{title}</div>
        <div style={{ fontSize: 12, color: '#666' }}>
          {safeValue == null ? (
            '身高/体重数据缺失'
          ) : (
            <>
              当前 BMI：
              <span style={{ fontWeight: 600, color: '#fa541c' }}>{safeValue.toFixed(1)}</span>
              {unit && <span style={{ marginLeft: 4 }}>{unit}</span>}
              <span style={{ marginLeft: 4, color: '#999' }}>（{categoryLabel}）</span>
            </>
          )}
        </div>
      </div>

      {/* 主体色带 + 指示器 */}
      <div style={{ position: 'relative', height: 32, margin: '8px 4px 4px' }}>
        {/* 底色条：模拟示例图的渐变管道 */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 10,
            height: 8,
            borderRadius: 999,
            background:
              'linear-gradient(90deg, #ff4d4f 0%, #ffec3d 20%, #52c41a 45%, #faad14 70%, #f5222d 100%)',
          }}
        />

        {/* 细分区间：在渐变条上再叠一层轻微色块，帮助分辨区段 */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 10,
            height: 8,
            borderRadius: 999,
            overflow: 'hidden',
            display: 'flex',
          }}
        >
          {thresholds.map(t => {
            const widthPercent = ((t.end - t.start) / (max - min)) * 100;
            return (
              <div
                key={t.key}
                style={{
                  width: `${widthPercent}%`,
                  background: 'transparent',
                  borderRight: '1px solid rgba(255,255,255,0.6)',
                }}
              />
            );
          })}
        </div>

        {/* 指示器 */}
        {safeValue != null && (
          <div
            style={{
              position: 'absolute',
              left: `${percent}%`,
              top: 0,
              transform: 'translateX(-50%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: '#fff',
                border: '2px solid #f5222d',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 4px rgba(0,0,0,0.15)',
                marginBottom: 2,
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f5222d' }} />
            </div>
            <div style={{ width: 1, height: 10, background: '#f5222d' }} />
          </div>
        )}
      </div>

      {/* 区间中文标签 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 12,
          color: '#666',
          margin: '4px 4px 0',
        }}
      >
        {thresholds.map(t => (
          <div
            key={t.key}
            style={{
              flex: 1,
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            {t.label}
          </div>
        ))}
      </div>

      {/* 下方刻度与数值：只在关键位置显示数字，整体更干净 */}
      {showTicks && (
        <div style={{ position: 'relative', marginTop: 8, padding: '0 24px 2px' }}>
          <div style={{ position: 'relative', height: 18 }}>
            {ticks.map(t => {
              const p = ((t - min) / (max - min)) * 100;
              const isMajor = majorTickValues.has(Number(t.toFixed(1)));
              return (
                <div
                  key={t}
                  style={{
                    position: 'absolute',
                    left: `${p}%`,
                    transform: 'translateX(-50%)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <div
                    style={{
                      width: 1,
                      height: isMajor ? 10 : 6,
                      background: isMajor ? '#999' : '#ccc',
                    }}
                  />
                  {isMajor && (
                    <div style={{ fontSize: 10, marginTop: 2, whiteSpace: 'nowrap', transform: isMajor && t === max ? 'translateX(-4px)' : 'none' }}>{t}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default BMIScale;

